import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getPaymentStatus } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const navigate = useNavigate();
  
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (pollCount >= 5) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await getPaymentStatus(sessionId);
      const data = response.data;
      
      setPaymentInfo(data);
      
      if (data.payment_status === "paid") {
        setStatus("success");
        toast.success("Paiement réussi !");
      } else if (data.status === "expired") {
        setStatus("expired");
      } else {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error("Error checking payment:", error);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F5] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white border-[#E5E0DC]">
        <CardContent className="p-8 text-center">
          {status === "loading" && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin mb-4" />
              <h1 className="font-heading text-2xl text-secondary mb-2">
                Vérification du paiement...
              </h1>
              <p className="text-muted-foreground">
                Veuillez patienter pendant que nous confirmons votre paiement.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto bg-status-success/10 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-12 h-12 text-status-success" />
              </div>
              <h1 className="font-heading text-2xl text-secondary mb-2">
                Paiement réussi !
              </h1>
              <p className="text-muted-foreground mb-6">
                Merci pour votre paiement de {paymentInfo?.amount?.toFixed(2)} €.
                Votre compte a été mis à jour.
              </p>
              <Button
                onClick={() => navigate("/")}
                className="bg-primary hover:bg-primary-hover"
              >
                Retour au tableau de bord
              </Button>
            </>
          )}

          {(status === "error" || status === "expired" || status === "timeout") && (
            <>
              <div className="w-20 h-20 mx-auto bg-status-error/10 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-12 h-12 text-status-error" />
              </div>
              <h1 className="font-heading text-2xl text-secondary mb-2">
                {status === "timeout" 
                  ? "Délai dépassé" 
                  : status === "expired" 
                    ? "Session expirée" 
                    : "Erreur de paiement"}
              </h1>
              <p className="text-muted-foreground mb-6">
                {status === "timeout"
                  ? "La vérification du paiement prend plus de temps que prévu. Vérifiez votre email pour confirmation."
                  : "Une erreur s'est produite. Veuillez réessayer ou contacter le support."}
              </p>
              <Button
                onClick={() => navigate("/mes-paiements")}
                variant="outline"
                className="mr-2"
              >
                Retour aux cotisations
              </Button>
              <Button
                onClick={() => navigate("/")}
                className="bg-primary hover:bg-primary-hover"
              >
                Tableau de bord
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
